import { useEffect } from "react";

const GLASS_SURFACE = ".glass:not(input):not(select):not(textarea)";

/**
 * Gives glass panels a local highlight that follows a fine-pointer cursor.
 * The effect stays CSS-driven and is intentionally disabled for touch screens.
 */
const GlassPointer = () => {
  useEffect(() => {
    const canHover = window.matchMedia("(hover: hover) and (pointer: fine)");
    if (!canHover.matches) return undefined;

    let activeSurface = null;
    let animationFrame = null;
    let pointer = { x: 0, y: 0 };

    const clearSurface = (surface) => {
      if (!surface) return;
      surface.classList.remove("glass--lit");
      surface.style.removeProperty("--glass-x");
      surface.style.removeProperty("--glass-y");
    };

    const paintHighlight = () => {
      animationFrame = null;
      if (!activeSurface) return;

      const rect = activeSurface.getBoundingClientRect();
      const x = ((pointer.x - rect.left) / rect.width) * 100;
      const y = ((pointer.y - rect.top) / rect.height) * 100;

      activeSurface.style.setProperty("--glass-x", `${Math.max(0, Math.min(100, x))}%`);
      activeSurface.style.setProperty("--glass-y", `${Math.max(0, Math.min(100, y))}%`);
      activeSurface.classList.add("glass--lit");
    };

    const handleMove = (event) => {
      const target = event.target instanceof Element ? event.target.closest(GLASS_SURFACE) : null;

      if (target !== activeSurface) {
        clearSurface(activeSurface);
        activeSurface = target;
      }

      if (!activeSurface) return;

      pointer = { x: event.clientX, y: event.clientY };
      if (animationFrame === null) animationFrame = requestAnimationFrame(paintHighlight);
    };

    const handleOut = (event) => {
      if (!activeSurface) return;
      const nextSurface = event.relatedTarget instanceof Element
        ? event.relatedTarget.closest(GLASS_SURFACE)
        : null;

      if (nextSurface === activeSurface) return;
      clearSurface(activeSurface);
      activeSurface = null;
    };

    document.addEventListener("pointermove", handleMove, { passive: true });
    document.addEventListener("pointerout", handleOut, { passive: true });

    return () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerout", handleOut);
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);
      clearSurface(activeSurface);
    };
  }, []);

  return null;
};

export default GlassPointer;
